// File: src/axiosGlobal.js
import axios from 'axios';
import { handleLogout } from '../context/AuthContext'; // Import your handleLogout

/**
 * 1) Set up Axios defaults:
 *    - Base URL
 */
axios.defaults.baseURL = `${process.env.REACT_APP_API_URL}`;
console.log('[axiosGlobal] Base URL set to:', axios.defaults.baseURL);

/**
 * 2) REQUEST INTERCEPTOR:
 *    - Attach short-lived access token (from localStorage) to Authorization header
 */
axios.interceptors.request.use(
  (config) => {
    if (config.headers && config.headers.Authorization) {
      // If header is already set explicitly, don't overwrite
      return config;
    }

    // Check if this is an admin call
    const isAdminCall =
      config.url?.startsWith('/admin') || config.url?.includes('/admin/');

    // Grab user token & admin token
    const adminToken = localStorage.getItem('adminToken');
    const userToken = localStorage.getItem('accessToken');

    const chosenToken = isAdminCall ? adminToken : userToken;
    if (chosenToken) {
      config.headers.Authorization = `Bearer ${chosenToken}`;
    }

    return config;
  },
  (error) => {
    console.warn('[axiosGlobal:requestInterceptor] Request error =>', error);
    return Promise.reject(error);
  }
);

/**
 * 3) Concurrency control for token refresh:
 */
let isRefreshing = false;
let refreshSubscribers = [];

// Notify all queued subscribers that a new token is available
function onRefreshed(newToken) {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
}

// Add a request's callback to the subscriber queue
function addRefreshSubscriber(callback) {
  refreshSubscribers.push(callback);
}

/**
 * 4) RESPONSE INTERCEPTOR:
 *    - If we receive a 401 => attempt to refresh once, then retry.
 *    - If refresh fails => call handleLogout() to force user out.
 */
let refreshingPromise = null;

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (!error.response) {
      console.warn('[axiosGlobal:responseInterceptor] No response => network error', error);
      return Promise.reject(error);
    }

    const { config, response } = error;
    const { status } = response;

    // Normalize pathname (works for absolute or relative URLs)
    let pathname = '';
    try {
      const base = axios.defaults.baseURL || '';
      pathname = new URL(config.url, base).pathname || '';
    } catch {
      pathname = config.url || '';
    }

    const isAdminCall   = pathname.startsWith('/admin');
    const isAuthRefresh = pathname.startsWith('/auth/refresh');
    const isAuthLogin   = pathname.startsWith('/auth/login');

    // Admin calls → just boot to admin login on 401
    if (isAdminCall && status === 401) {
      localStorage.removeItem('adminToken');
      window.location.href = '/admin/login';
      return Promise.reject(error);
    }

    // Never try to refresh on refresh/login endpoints
    if (isAuthRefresh || isAuthLogin) {
      return Promise.reject(error);
    }

    if (status === 401 && !config._retry) {
      config._retry = true;

      // Create (or reuse) a single in-flight refresh
      if (!refreshingPromise) {
        const rt = localStorage.getItem('refreshToken');
        if (!rt) {
          // No refresh token → force logout
          try {
            const { handleLogout } = await import('../context/AuthContext');
            handleLogout?.();
          } catch {}
          return Promise.reject(error);
        }

        refreshingPromise = axios
          .post('/auth/refresh', { refreshToken: rt })
          .then(({ data }) => {
            const { accessToken: newAccess, refreshToken: newRefresh } = data || {};
            if (newAccess)   localStorage.setItem('accessToken',  newAccess);
            if (newRefresh)  localStorage.setItem('refreshToken', newRefresh);
            return newAccess;
          })
          .catch(async (err) => {
            console.error('[axiosGlobal:responseInterceptor] Refresh error => logging out.', err);
            try {
              const { handleLogout } = await import('../context/AuthContext');
              handleLogout?.();
            } catch {}
            throw err;
          })
          .finally(() => {
            // allow future refreshes
            setTimeout(() => { refreshingPromise = null; }, 0);
          });
      }

      // Wait for refresh to complete, then retry original request
      try {
        const newToken = await refreshingPromise;
        const token = newToken || localStorage.getItem('accessToken');
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        }
        return axios(config);
      } catch {
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);


export default axios;
